import React from "react";
import { Moon, Sparkles, Sun } from "lucide-react";
import { useApp } from "../context/AppContext";
import { Logo } from "./Logo";

const SERVICES = [
  { id: "dinner", icon: Moon, glow: "from-primary/30" },
  { id: "lunch", icon: Sun, glow: "from-primary/25" },
];

export function ServiceStep({ onSelect, onEvent }) {
  const { t, theme } = useApp();
  const woodClass = theme === "dark" ? "wood-grain" : "wood-grain-light";

  return (
    <section
      className={`relative overflow-hidden rounded-3xl border border-border ${woodClass} firefly-grain`}
      data-testid="service-step"
    >
      <div className="relative px-6 py-12 sm:px-10 sm:py-16">
        {/* Floating fireflies */}
        <span className="pointer-events-none absolute left-[10%] top-[12%] h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_14px_4px_hsl(var(--primary)/0.6)] animate-firefly-float" />
        <span className="pointer-events-none absolute right-[12%] top-[28%] h-1 w-1 rounded-full bg-primary/80 shadow-[0_0_10px_3px_hsl(var(--primary)/0.55)] animate-firefly-float [animation-delay:1.2s]" />
        <span className="pointer-events-none absolute left-[80%] bottom-[18%] h-1 w-1 rounded-full bg-primary/70 shadow-[0_0_10px_3px_hsl(var(--primary)/0.5)] animate-firefly-float [animation-delay:2.1s]" />

        <div className="mx-auto max-w-3xl text-center">
          <div className="mx-auto flex justify-center">
            <Logo size={120} />
          </div>
          <p className="mt-3 font-display italic text-primary/95 text-lg sm:text-xl">
            {t("tagline")}
          </p>
          <h2 className="mt-6 font-display text-xl font-semibold tracking-tight sm:text-2xl">
            {t("landing_choose_service")}
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground sm:text-base">
            {t("landing_description")}
          </p>

          <div className="mt-10 grid gap-4 sm:mx-auto sm:max-w-xl sm:grid-cols-2">
            {SERVICES.map(({ id, icon: Icon, glow }, idx) => (
              <button
                key={id}
                type="button"
                onClick={() => onSelect(id)}
                data-testid={`service-card-${id}`}
                className="group relative flex flex-col items-center gap-3 overflow-hidden rounded-2xl border border-border bg-card/80 px-5 py-7 text-center backdrop-blur-sm transition-all hover:-translate-y-1 hover:border-primary/60 hover:shadow-[0_20px_50px_-15px_hsl(var(--primary)/0.45)] animate-fade-slide-up"
                style={{ animationDelay: `${idx * 80}ms` }}
              >
                <span
                  className={`pointer-events-none absolute inset-0 bg-gradient-to-b ${glow} to-transparent opacity-0 transition-opacity group-hover:opacity-100`}
                />
                <span className="relative flex h-14 w-14 items-center justify-center rounded-full border border-primary/40 bg-primary/10 text-primary transition-transform group-hover:scale-110">
                  <Icon className="h-6 w-6" />
                </span>
                <span className="relative font-display text-lg font-semibold tracking-wide">
                  {t(`service_${id}`)}
                </span>
                <span className="relative text-xs uppercase tracking-[0.2em] text-primary">
                  {t(`service_${id}_hours`)}
                </span>
                <span className="relative text-sm text-muted-foreground">
                  {t(`service_${id}_subtitle`)}
                </span>
              </button>
            ))}
          </div>

          {/* Private events CTA — visually distinct, lower hierarchy than the 3 cards */}
          <button
            type="button"
            onClick={onEvent}
            data-testid="event-cta-button"
            className="group mt-6 flex w-full items-center justify-between gap-4 rounded-2xl border border-primary/40 bg-card/40 px-5 py-4 text-left backdrop-blur-sm transition-all hover:border-primary hover:bg-primary/10 hover:shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.5)] animate-fade-slide-up sm:px-7"
            style={{ animationDelay: "320ms" }}
          >
            <span className="flex items-center gap-3 sm:gap-4">
              <span className="relative flex h-10 w-10 items-center justify-center rounded-full border border-primary/50 bg-primary/15 text-primary">
                <Sparkles className="h-5 w-5" />
              </span>
              <span className="flex flex-col">
                <span className="font-display text-base font-semibold tracking-[0.18em] uppercase sm:text-lg">
                  {t("cta_event_label")}
                </span>
                {t("cta_event_subtitle") && (
                  <span className="text-xs text-muted-foreground sm:text-sm">
                    {t("cta_event_subtitle")}
                  </span>
                )}
              </span>
            </span>
            <span className="text-primary transition-transform group-hover:translate-x-1">→</span>
          </button>
        </div>
      </div>
    </section>
  );
}
